/**
 * O que faz: Ranking dos links personalizados por leads gerados, com barras proporcionais e drill-down.
 * Onde usa: Dashboard principal — seção de gráficos.
 * Conexões: Recebe dados de performance via props e chama onLinkClick para abrir o modal de leads.
 * Tabelas: Consome dados agregados da coluna link_name da tabela leads (via dashboardService).
 * Data: 11/06/2026
 */

import { Link, ArrowUpRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface LinkPerformanceData {
  linkName: string;
  leads: number;
  conversoes: number;
  source: string;
}

interface CustomLinksCardProps {
  data: LinkPerformanceData[];
  onLinkClick?: (linkName: string) => void;
  adminMode?: boolean;
  targetClinicaId?: string;
}

const SOURCE_LABELS: Record<string, string> = {
  instagram_bio: 'Instagram Bio',
  instagram_stories: 'Instagram Stories',
  whatsapp_status: 'WhatsApp Status',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
  email_signature: 'Email',
  website: 'Website',
  qr_code: 'QR Code',
  outros: 'Outros',
};

export const CustomLinksCard = ({
  data = [],
  onLinkClick,
  adminMode = false,
  targetClinicaId
}: CustomLinksCardProps) => {
  const totalLeads = data.reduce((sum, item) => sum + item.leads, 0);
  const totalConversoes = data.reduce((sum, item) => sum + item.conversoes, 0);
  const taxaConversaoGeral = totalLeads > 0 ? (totalConversoes / totalLeads) * 100 : 0;
  const displayData = data.slice(0, 5);
  const maxLeads = displayData.reduce((max, item) => Math.max(max, item.leads), 0);

  const getSourceLabel = (source: string) => SOURCE_LABELS[source] || source;

  return (
    <div className="flex flex-col bg-card rounded-2xl border border-border p-4 sm:p-6 shadow-[0_1px_2px_rgba(19,41,27,0.05),0_3px_10px_rgba(19,41,27,0.05)]">
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-[15px] font-semibold text-foreground">Links Personalizados</h3>
        <p className="mt-0.5 text-[13px] text-muted-foreground">
          Performance dos seus links rastreáveis
          {adminMode && targetClinicaId && ' (modo admin)'}
        </p>
      </div>

      {data.length === 0 ? (
        <div className="flex flex-1 items-center justify-center py-10">
          <div className="text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-muted">
              <Link className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
            </div>
            <p className="text-sm text-muted-foreground">Nenhum lead de links ainda</p>
            <p className="mt-1 text-xs text-muted-foreground/80">
              Configure links personalizados para começar a rastrear
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Lista de links com barras proporcionais */}
          <div className="flex-1 space-y-1">
            {displayData.map((link, index) => {
              const taxaConversao = link.leads > 0 ? (link.conversoes / link.leads) * 100 : 0;
              const share = maxLeads > 0 ? (link.leads / maxLeads) * 100 : 0;
              const isClickable = !!onLinkClick;

              return (
                <div
                  key={link.linkName}
                  className={`group rounded-xl px-3 py-2.5 transition-colors duration-200 ${
                    isClickable ? 'cursor-pointer hover:bg-accent/60' : ''
                  }`}
                  onClick={() => onLinkClick?.(link.linkName)}
                  role={isClickable ? 'button' : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && isClickable) {
                      e.preventDefault();
                      onLinkClick?.(link.linkName);
                    }
                  }}
                >
                  <div className="flex items-center gap-2">
                    <span className="w-4 shrink-0 text-xs font-semibold tabular-nums text-muted-foreground">
                      {index + 1}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground" title={link.linkName}>
                      {link.linkName}
                    </span>
                    <Badge
                      variant="secondary"
                      className="hidden shrink-0 rounded-full px-2 py-0 text-[10px] font-medium sm:inline-flex"
                    >
                      {getSourceLabel(link.source)}
                    </Badge>
                    <span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">
                      {link.leads}
                    </span>
                    {isClickable && (
                      <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-accent-foreground opacity-0 transition-opacity duration-200 group-hover:opacity-100" aria-hidden="true" />
                    )}
                  </div>

                  <div className="mt-1.5 flex items-center gap-2 pl-6">
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-primary transition-all duration-500"
                        style={{ width: `${share}%` }}
                      />
                    </div>
                    <span className="w-14 shrink-0 text-right text-[11px] tabular-nums text-muted-foreground">
                      {taxaConversao.toFixed(0)}% conv.
                    </span>
                  </div>
                </div>
              );
            })}

            {data.length > 5 && (
              <p className="px-3 pt-1 text-xs text-muted-foreground">
                E mais {data.length - 5} {data.length - 5 === 1 ? 'link' : 'links'}
              </p>
            )}
          </div>

          {/* Resumo */}
          <div className="mt-4 flex items-center gap-6 border-t border-border pt-3">
            <div className="flex items-baseline gap-1.5">
              <span className="text-lg font-semibold tabular-nums text-foreground">{totalLeads}</span>
              <span className="text-xs text-muted-foreground">leads</span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-lg font-semibold tabular-nums text-foreground">{totalConversoes}</span>
              <span className="text-xs text-muted-foreground">conversões</span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-lg font-semibold tabular-nums text-foreground">{taxaConversaoGeral.toFixed(1)}%</span>
              <span className="text-xs text-muted-foreground">taxa</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
